// src/pages/Products.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import API from "../api/axios";
import { toast } from "react-toastify";

import "../styles/product.css";

const Products = () => {

  /* =====================================================
     STATES
  ===================================================== */

  const [products, setProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [uploading, setUploading] =
    useState(false);

  const [editingId, setEditingId] =
    useState(null);

  const [search, setSearch] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const [pages, setPages] =
    useState(1);

  /* =====================================================
     DEFAULT FORM
  ===================================================== */

  const defaultForm = {

    /* BASIC */

    name: "",

    brand: "SAC MTB",

    modelNumber: "",

    category: "Bicycle",

    type: "MTB",

    badge: "",

    status: "active",

    displayOrder: 0,

    /* CONTENT */

    description: "",

    heroTitle: "",

    heroSubtitle: "",

    heroDescription: "",

    storyTitle: "",

    storyDescription: "",

    /* PRICING */

    price: "",

    discountPrice: "",

    stock: "",

    deliveryInfo:
      "Free Delivery Available",

    warranty: "",

    /* MEDIA */

    thumbnail: "",

    imageUrls: [],

    videoUrl: "",

    experienceImages: [],

    experienceVideo: "",

    rotationSpeed: 30,

    /* FEATURES */

    features: [
      {
        title: "",
        desc: "",
        icon: "",
      },
    ],

    bulletPoints: [""],

    /* VARIANTS */

    sizes: [
      {
        size: "",
        price: "",
        stock: "",
        sku: "",
      },
    ],

    /* COLORS */

    colorOptions: [
      {
        name: "",
        code: "#ffffff",
        images: [],
      },
    ],

    /* SPECIFICATIONS */

    specifications: {},

    /* THEME */

    themeColor: "#9dff00",

    secondaryColor: "#050505",

    accentColor: "#ffffff",

    themeMode: "dark",

    stickyEnabled: true,

    /* CONTROLS */

    isFeatured: false,

    showInHomepage: false,

    showStorySection: true,

    showExperienceSection: true,

    showReviewsSection: true,

    showSpecificationsSection: true,

    /* SEO */

    seoTitle: "",

    seoDescription: "",

    seoKeywords: "",
  };

  const [form, setForm] =
    useState(defaultForm);

  /* =====================================================
     FETCH PRODUCTS
  ===================================================== */

  const fetchProducts =
  async(page = 1)=>{

    try {

      setLoading(true);

      const { data } =
        await API.get(
          `/products?page=${page}&limit=8`
        );

      setProducts(
        data.products || []
      );

      setPages(
        data.pages || 1
      );

      setCurrentPage(page);

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to load products"
      );

    } finally {

      setLoading(false);
    }
  };

  useEffect(()=>{

    fetchProducts(1);

  }, []);

  /* =====================================================
     SEARCH
  ===================================================== */

  const filteredProducts =
    useMemo(()=>{

      return products.filter((p)=>

        p.name
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          )
      );

    }, [products, search]);

  /* =====================================================
     COMMON CHANGE
  ===================================================== */

  const handleChange =
  (e)=>{

    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev)=>({

      ...prev,

      [name]:

        type === "checkbox"
          ? checked
          : value,
    }));
  };

  /* =====================================================
     FEATURES
  ===================================================== */

  const handleFeatureChange =
  (index,key,value)=>{

    const updated =
      [...form.features];

    updated[index][key] =
      value;

    setForm({

      ...form,

      features: updated,
    });
  };

  const addFeature = ()=>{

    setForm({

      ...form,

      features: [

        ...form.features,

        {
          title:"",
          desc:"",
          icon:"",
        },
      ],
    });
  };

  const removeFeature =
  (index)=>{

    const updated =
      [...form.features];

    updated.splice(index,1);

    setForm({

      ...form,

      features: updated,
    });
  };

  /* =====================================================
     VARIANTS
  ===================================================== */

  const handleVariantChange =
  (index,key,value)=>{

    const updated =
      [...form.sizes];

    updated[index][key] =
      value;

    setForm({

      ...form,

      sizes: updated,
    });
  };

  const addVariant = ()=>{

    setForm({

      ...form,

      sizes: [

        ...form.sizes,

        {
          size:"",
          price:"",
          stock:"",
          sku:"",
        },
      ],
    });
  };

  const removeVariant =
  (index)=>{

    const updated =
      [...form.sizes];

    updated.splice(index,1);

    setForm({

      ...form,

      sizes: updated,
    });
  };

  /* =====================================================
     COLORS
  ===================================================== */

  const handleColorChange =
  (index,key,value)=>{

    const updated =
      [...form.colorOptions];

    updated[index][key] =
      value;

    setForm({

      ...form,

      colorOptions: updated,
    });
  };

  const addColorOption = ()=>{

    setForm({

      ...form,

      colorOptions: [

        ...form.colorOptions,

        {
          name:"",
          code:"#ffffff",
          images:[],
        },
      ],
    });
  };

  const removeColorOption =
  (index)=>{

    const updated =
      [...form.colorOptions];

    updated.splice(index,1);

    setForm({

      ...form,

      colorOptions: updated,
    });
  };

  /* =====================================================
     IMAGE UPLOAD
  ===================================================== */

  const handleImageUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);

    if (files.length === 0) return;

    console.log("[UPLOAD DEBUG] Upload started");

    console.log(
      "[UPLOAD DEBUG] Selected files:",
      files.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      }))
    );

    if (files.length > 10) {
      toast.error("You can upload a maximum of 10 images at once");
      input.value = "";
      return;
    }

    const oversizedFile =
      files.find((file) => file.size > 5 * 1024 * 1024);

    if (oversizedFile) {
      toast.error(`${oversizedFile.name} exceeds the 5MB upload limit`);
      input.value = "";
      return;
    }

    const invalidFile =
      files.find((file) => !file.type.startsWith("image/"));

    if (invalidFile) {
      toast.error(`${invalidFile.name} is not a valid image`);
      input.value = "";
      return;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("images", file, file.name);
    });

    console.log("[UPLOAD DEBUG] FormData contents:");

    for (const [field, value] of formData.entries()) {
      console.log("[UPLOAD DEBUG] FormData entry:", {
        field,
        value,
        name: value.name,
        size: value.size,
        type: value.type,
        lastModified: value.lastModified,
      });
    }

    setUploading(true);

    try {
      console.log("[UPLOAD DEBUG] Before API.post()", {
        url: "/upload",
        method: "POST",
        baseURL: API.defaults.baseURL,
        timeout: 60000,
        totalFiles: files.length,
      });

      const { data, status } = await API.post(
        "/upload",
        formData,
        {
          timeout: 60000,
        }
      );

      console.log("[UPLOAD DEBUG] Upload response received", {
        status,
        responseBody: data,
        imageUrls: data.imageUrls,
      });

      setForm((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...data.imageUrls],
        thumbnail:
          prev.thumbnail || data.imageUrls[0],
      }));

      toast.success("Images Uploaded");
    } catch (err) {
      console.error("[UPLOAD DEBUG] Complete upload error object", err);
      console.error("[UPLOAD DEBUG] Upload failed", {
        message: err.message,
        stack: err.stack,
        code: err.code,
        responseStatus: err.response?.status,
        responseData: err.response?.data,
        responseHeaders: err.response?.headers,
        requestConfig: err.config,
        request: err.request,
        requestURL: err.config?.url,
        requestMethod: err.config?.method,
      });

      toast.error(
        err.response?.data?.message ||
        err.message ||
        "Image upload failed"
      );
    } finally {
      console.log("[UPLOAD DEBUG] Upload finished");
      setUploading(false);
      input.value = "";
    }
  };

  /* =====================================================
     REMOVE IMAGE
  ===================================================== */

  const removeImage =
  (index)=>{

    const updated =
      [...form.imageUrls]; 

    updated.splice(index,1);

    setForm({

      ...form,

      imageUrls: updated,
    });
  };

  /* =====================================================
     HANDLE SUBMIT
  ===================================================== */

  const handleSubmit =
  async(e)=>{

    e.preventDefault();

    try {

      const payload = {

        ...form,

        price:
          Number(form.price) || 0,

        discountPrice:
          Number(
            form.discountPrice
          ) || 0,

        stock:
          Number(form.stock) || 0,

        displayOrder:
          Number(
            form.displayOrder
          ) || 0,

        rotationSpeed:
          Number(
            form.rotationSpeed
          ) || 30,

        seoKeywords:

          typeof form.seoKeywords ===
          "string"

            ? form.seoKeywords
                .split(",")
                .map((k)=>k.trim())
                .filter(Boolean)

            : [],

        features:

          form.features

            .filter(
              (f)=>

                f.title?.trim()

                ||

                f.desc?.trim()

                ||

                f.icon?.trim()
            )

            .map((f)=>({

              title:
                f.title || "",

              desc:
                f.desc || "",

              icon:
                f.icon || "",
            })),

        bulletPoints:

          form.bulletPoints.filter(
            (b)=>b.trim()
          ),

        sizes:

          form.sizes

            .filter(
              (item)=>
                item.size?.trim()
            )

            .map((item)=>({

              size:
                item.size || "",

              price:

                Number(item.price)

                ||

                Number(form.price)

                ||

                0,

              stock:

                Number(item.stock)

                ||

                Number(form.stock)

                ||

                0,

              sku:
                item.sku || "",
            })),

        colorOptions:

          form.colorOptions.filter(
            (c)=>

              c.name?.trim()

              ||

              c.code
          ),

        thumbnail:

          form.thumbnail ||

          form.imageUrls?.[0] ||

          "",

        status:
          form.status || "active",
      };

      if(editingId){

        await API.put(
          `/products/${editingId}`,
          payload
        );

        toast.success(
          "✅ Product Updated"
        );

      } else {

        await API.post(
          "/products",
          payload
        );

        toast.success(
          "✅ Product Created"
        );
      }

      resetForm();

      fetchProducts(currentPage);

    } catch (error) {

      console.error(error);

      toast.error(
        error?.response?.data?.message ||
        "❌ Action Failed"
      );
    }
  };

  /* =====================================================
     RESET
  ===================================================== */

  const resetForm = ()=>{

    setForm(defaultForm);

    setEditingId(null);
  };

  /* =====================================================
     EDIT
  ===================================================== */

  const handleEdit = async (id) => {
  try {
    const { data } = await API.get(`/products/${id}`);

    setForm({
      ...defaultForm,
      ...data,

      seoKeywords: Array.isArray(data.seoKeywords)
        ? data.seoKeywords.join(",")
        : "",

      features: data.features?.length
        ? data.features
        : defaultForm.features,

      sizes: data.sizes?.length
        ? data.sizes
        : defaultForm.sizes,

      colorOptions: data.colorOptions?.length
        ? data.colorOptions
        : defaultForm.colorOptions,

      bulletPoints: data.bulletPoints?.length
        ? data.bulletPoints
        : [""],

      specifications: data.specifications || {},

      imageUrls: data.imageUrls || [],

      experienceImages: data.experienceImages || [],
    });

    setEditingId(data._id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  } catch (err) {
    console.error(err);
    toast.error("Failed to load product");
  }
};

  /* =====================================================
     DELETE
  ===================================================== */

  const handleDelete =
  async(id)=>{

    if(
      !window.confirm(
        "Delete this product?"
      )
    ) return;

    try {

      await API.delete(
        `/products/${id}`
      );

      toast.success(
        "Product Deleted"
      );

      fetchProducts(currentPage);

    } catch (error) {

      console.error(error);

      toast.error(
        "Delete Failed"
      );
    }
  };

  return (

    <div className="admin-container modern">

      <div className="admin-header">

        <h2>
          🛍 Product Management
        </h2>

      </div>

      <div className="search-box">

        <input
          type="text"
          placeholder="Search Product..."
          value={search}
          onChange={(e)=>
            setSearch(
              e.target.value
            )
          }
        />

      </div>

      <form
        className="product-form-modern"
        onSubmit={handleSubmit}
      >

        {/* PRODUCT INFO */}

        <section className="card">

          <h3>
            📦 Product Info
          </h3>

          <input
            name="name"
            placeholder="Product Name"
            value={form.name}
            onChange={handleChange}
            required
          />

          <select
            name="category"
            value={form.category}
            onChange={handleChange}
          >

            <option value="Bicycle">
              Bicycle
            </option>

            <option value="Toys">
              Toys
            </option>

          </select>

          <select
            name="type"
            value={form.type}
            onChange={handleChange}
          >

            {

              form.category === "Toys"

              ? (

                <>

                  <option value="Swing Car">
                    Swing Car
                  </option>

                  <option value="Ride On">
                    Ride On
                  </option>

                  <option value="Jeep">
                    Jeep
                  </option>

                  <option value="ATV">
                    ATV
                  </option>
                  <option value="Bike">
                    Bike
                  </option>
                  <option value="Tricycle">
                    Tricycle
                  </option>
                  <option value="Stroller">
                    Stroller
                  </option>

                </>

              )

              : (

                <>

                  <option value="MTB">
                    MTB
                  </option>

                  <option value="Road Bike">
                    Road Bike
                  </option>

                  <option value="Kids Cycle">
                    Kids Cycle
                  </option>

                </>

              )
            }

          </select>

          <textarea
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
          />

          <div className="price-row">

            <input
              type="number"
              name="price"
              placeholder="MRP"
              value={form.price}
              onChange={handleChange}
            />

            <input
              type="number"
              name="discountPrice"
              placeholder="Sale Price"
              value={form.discountPrice}
              onChange={handleChange}
            />

            <input
              type="number"
              name="stock"
              placeholder="Stock"
              value={form.stock}
              onChange={handleChange}
            />

          </div>

        </section>

        {/* VARIANTS */}

        <section className="card">

          <h3>
            📏 Product Variants
          </h3>

          {

            form.sizes.map(
              (item,index)=>(

              <div
                className="variant-grid"
                key={index}
              >

                <input
                  placeholder={
                    form.category ===
                    "Toys"

                    ? "1 Seater"

                    : "20T"
                  }

                  value={item.size}

                  onChange={(e)=>

                    handleVariantChange(
                      index,
                      "size",
                      e.target.value
                    )
                  }
                />

                <input
                  type="number"
                  placeholder="Price"
                  value={item.price}
                  onChange={(e)=>

                    handleVariantChange(
                      index,
                      "price",
                      e.target.value
                    )
                  }
                />

                <input
                  type="number"
                  placeholder="Stock"
                  value={item.stock}
                  onChange={(e)=>

                    handleVariantChange(
                      index,
                      "stock",
                      e.target.value
                    )
                  }
                />

                <input
                  placeholder="SKU"
                  value={item.sku}
                  onChange={(e)=>

                    handleVariantChange(
                      index,
                      "sku",
                      e.target.value
                    )
                  }
                />

                <button
                  type="button"
                  onClick={()=>
                    removeVariant(index)
                  }
                >

                  Remove

                </button>

              </div>
            ))
          }

          <button
            type="button"
            onClick={addVariant}
          >

            + Add Variant

          </button>

        </section>
<section className="card">

  <h3>
    🎬 Hero Section
  </h3>

  <input
    name="heroTitle"
    placeholder="Hero Title"
    value={form.heroTitle}
    onChange={handleChange}
  />

  <input
    name="heroSubtitle"
    placeholder="Hero Subtitle"
    value={form.heroSubtitle}
    onChange={handleChange}
  />

  <textarea
    name="heroDescription"
    placeholder="Hero Description"
    value={form.heroDescription}
    onChange={handleChange}
  />

</section>

<section className="card">

  <h3>
    📖 Story Section
  </h3>

  <input
    name="storyTitle"
    placeholder="Story Title"
    value={form.storyTitle}
    onChange={handleChange}
  />

  <textarea
    name="storyDescription"
    placeholder="Story Description"
    value={form.storyDescription}
    onChange={handleChange}
  />

</section>

<section className="card">

  <h3>
    🚚 Delivery & Warranty
  </h3>

  <input
    name="deliveryInfo"
    placeholder="Delivery Info"
    value={form.deliveryInfo}
    onChange={handleChange}
  />

  <input
    name="warranty"
    placeholder="Warranty"
    value={form.warranty}
    onChange={handleChange}
  />

</section>

<section className="card">

  <h3>
    ✨ Features
  </h3>

  {

    form.features.map(
      (feature,index)=>(

      <div
        className="feature-grid"
        key={index}
      >

        <input
          placeholder="Feature Title"
          value={feature.title}
          onChange={(e)=>

            handleFeatureChange(
              index,
              "title",
              e.target.value
            )
          }
        />

        <input
          placeholder="Description"
          value={feature.desc}
          onChange={(e)=>

            handleFeatureChange(
              index,
              "desc",
              e.target.value
            )
          }
        />

        <input
          placeholder="Icon"
          value={feature.icon}
          onChange={(e)=>

            handleFeatureChange(
              index,
              "icon",
              e.target.value
            )
          }
        />

        <button
          type="button"
          onClick={()=>
            removeFeature(index)
          }
        >

          Remove

        </button>

      </div>
    ))
  }

  <button
    type="button"
    onClick={addFeature}
  >

    + Add Feature

  </button>

</section>

<section className="card">

  <h3>
    🎨 Color Options
  </h3>

  {

    form.colorOptions.map(
      (color,index)=>(

      <div
        className="variant-grid"
        key={index}
      >

        <input
          placeholder="Color Name"
          value={color.name}
          onChange={(e)=>

            handleColorChange(
              index,
              "name",
              e.target.value
            )
          }
        />

        <input
          type="color"
          value={color.code}
          onChange={(e)=>

            handleColorChange(
              index,
              "code",
              e.target.value
            )
          }
        />

        <button
          type="button"
          onClick={()=>
            removeColorOption(index)
          }
        >

          Remove

        </button>

      </div>
    ))
  }

  <button
    type="button"
    onClick={addColorOption}
  >

    + Add Color

  </button>

</section>

<section className="card">

  <h3>
    🔍 SEO
  </h3>

  <input
    name="seoTitle"
    placeholder="SEO Title"
    value={form.seoTitle}
    onChange={handleChange}
  />

  <textarea
    name="seoDescription"
    placeholder="SEO Description"
    value={form.seoDescription}
    onChange={handleChange}
  />

  <input
    name="seoKeywords"
    placeholder="SEO Keywords"
    value={form.seoKeywords}
    onChange={handleChange}
  />

</section>

<section className="card">

  <h3>
    🎨 Theme Settings
  </h3>

  <div className="price-row">

    <input
      type="color"
      name="themeColor"
      value={form.themeColor}
      onChange={handleChange}
    />

    <input
      type="color"
      name="secondaryColor"
      value={form.secondaryColor}
      onChange={handleChange}
    />

    <input
      type="color"
      name="accentColor"
      value={form.accentColor}
      onChange={handleChange}
    />

  </div>

</section>

<section className="card">

  <h3>
    ⚙ Controls
  </h3>

  <label>

    <input
      type="checkbox"
      name="isFeatured"
      checked={form.isFeatured}
      onChange={handleChange}
    />

    Featured Product

  </label>

  <label>

    <input
      type="checkbox"
      name="showInHomepage"
      checked={form.showInHomepage}
      onChange={handleChange}
    />

    Show In Homepage

  </label>

</section>

<section className="card">

  <h3>
    📌 Bullet Points
  </h3>

  {

    form.bulletPoints.map(
      (point,index)=>(

      <div
        className="variant-grid"
        key={index}
      >

        <input
          placeholder="Bullet Point"
          value={point}
          onChange={(e)=>{

            const updated = [
              ...form.bulletPoints
            ];

            updated[index] =
              e.target.value;

            setForm({

              ...form,

              bulletPoints:
                updated,
            });
          }}
        />

        <button
          type="button"
          onClick={()=>{

            const updated = [
              ...form.bulletPoints
            ];

            updated.splice(index,1);

            setForm({

              ...form,

              bulletPoints:
                updated,
            });
          }}
        >

          Remove

        </button>

      </div>
    ))
  }

  <button
    type="button"
    onClick={()=>{

      setForm({

        ...form,

        bulletPoints:[

          ...form.bulletPoints,

          "",
        ],
      });
    }}
  >

    + Add Bullet Point

  </button>

</section>

<section className="card">

  <h3>
    📋 Specifications
  </h3>

  <div className="variant-grid">

    <input
      placeholder="Frame Material"
      value={
        form.specifications
          ?.frame || ""
      }

      onChange={(e)=>

        setForm({

          ...form,

          specifications: {

            ...form.specifications,

            frame:
              e.target.value,
          },
        })
      }
    />

    <input
      placeholder="Brake Type"
      value={
        form.specifications
          ?.brake || ""
      }

      onChange={(e)=>

        setForm({

          ...form,

          specifications: {

            ...form.specifications,

            brake:
              e.target.value,
          },
        })
      }
    />

    <input
      placeholder="Wheel Size"
      value={
        form.specifications
          ?.wheel || ""
      }

      onChange={(e)=>

        setForm({

          ...form,

          specifications: {

            ...form.specifications,

            wheel:
              e.target.value,
          },
        })
      }
    />

    <input
      placeholder="Age Group"
      value={
        form.specifications
          ?.age || ""
      }

      onChange={(e)=>

        setForm({

          ...form,

          specifications: {

            ...form.specifications,

            age:
              e.target.value,
          },
        })
      }
    />

  </div>

</section>

<select
  name="status"
  value={form.status}
  onChange={handleChange}
>

  <option value="active">
    Active
  </option>

  <option value="draft">
    Draft
  </option>

  <option value="inactive">
    Inactive
  </option>

</select>


        {/* MEDIA */}

        <section className="card">

          <h3>
            🖼 Media
          </h3>

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={
              handleImageUpload
            }
            disabled={uploading}
          />

          <div className="image-grid">

            {

              form.imageUrls.map(
                (img,index)=>(

                <div
                  key={index}
                  className="image-box"
                >

                  <img
                    src={img}
                    alt=""
                    className="thumb"
                  />

                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={()=>
                      removeImage(index)
                    }
                  >

                    ×

                  </button>

                </div>
              ))
            }

          </div>

        </section>

        <button
          type="submit"
          className="submit-btn"
        >

          {

            editingId

              ? "UPDATE PRODUCT"

              : "CREATE PRODUCT"
          }

        </button>

      </form>

      {/* PRODUCT LIST */}

      <div className="products-grid-admin">

        {

          loading ? (

            <div className="loading">

              Loading Products...

            </div>

          ) : filteredProducts.length === 0 ? (

            <div className="empty-state">

              No Products Found

            </div>

          ) : (

            filteredProducts.map((p)=>(

              <div
                key={p._id}
                className="product-admin-card"
              >

                <img
                  src={
                    p.thumbnail ||
                    p.imageUrls?.[0]
                  }
                  alt={p.name}
                />

                <div className="product-card-content">

                  <h4>
                    {p.name}
                  </h4>

                  <p className="product-category">

                    {p.category}

                    {" • "}

                    {p.type}

                  </p>

                  <p className="product-price">

                    ₹
                    {
                      p.discountPrice ||
                      p.price
                    }

                  </p>

                  <div className="variant-preview">

                    {

                      p.sizes?.map(
                        (item,index)=>(

                        <span
                          key={index}
                        >

                          {item.size}

                        </span>
                      ))
                    }

                  </div>

                  <div className="stock-preview">

                    Stock:
                    {" "}
                    {p.stock}

                  </div>

                  <div className="admin-actions">

                    <button
                      type="button"
                      onClick={() => handleEdit(p._id)}
                    >

                      Edit

                    </button>

                    <button
                      type="button"
                      onClick={()=>
                        handleDelete(p._id)
                      }
                    >

                      Delete

                    </button>

                  </div>

                </div>

              </div>
            ))
          )
        }

      </div>

      {

        pages > 1 && (

          <div className="pagination">

            {

              [...Array(pages)].map(
                (_,index)=>(

                <button
                  key={index}

                  className={

                    currentPage ===
                    index + 1

                      ? "active"

                      : ""
                  }

                  onClick={()=>

                    fetchProducts(
                      index + 1
                    )
                  }
                >

                  {index + 1}

                </button>
              ))
            }

          </div>
        )
      }

    </div>
  );
};

export default Products;
